import { useMutation, useQuery } from '@tanstack/react-query';
import { createServerFn } from '@tanstack/react-start';
import {
  deleteCookie,
  getCookie,
  setCookie,
} from '@tanstack/react-start/server';
import z from 'zod';

import { fetchUser } from '@/features/course-progress/api/client';
import {
  impersonatedUserIdCookieKey,
  UserRoles,
} from '@/lib/constants/auth.constants';
import { handleServerFnError } from '@/lib/sentry-helpers';
import { standardMiddlewares } from '@/middlewares/standardMiddlewares';
import { assertUserHasRole } from '@/middlewares/userAuthMiddleware';

export const impersonatedUserQueryKey = ['impersonatedUser'];

export function useImpersonation() {
  const { mutateAsync: impersonate } = useMutation({
    mutationFn: setImpersonatedUserId,
    onSuccess: () => {
      // Refresh the page to consider a fresh user state
      window.location.reload();
    },
  });

  const {
    data: impersonatedUser,
    isLoading,
    error,
  } = useQuery({
    queryKey: impersonatedUserQueryKey,
    queryFn: async () => {
      const impersonatedUserId = await getImpersonatedUserId();
      if (!impersonatedUserId) {
        return null;
      }
      return fetchUser({ data: { id: impersonatedUserId } });
    },
  });

  return {
    impersonate,
    impersonatedUser,
    isLoading,
    error,
  };
}

const setImpersonatedUserId = createServerFn({
  method: 'GET',
})
  .middleware([standardMiddlewares])
  .validator(z.object({ userId: z.string().nullable() }))
  .handler(async ({ context, data }) => {
    const { cognitoUser, oneRosterApiTyped } = context;
    await assertUserHasRole(cognitoUser, oneRosterApiTyped, [
      UserRoles.ADMINISTRATOR,
      UserRoles.SYSTEM_ADMINISTRATOR,
      UserRoles.AIDE,
    ]);

    try {
      if (data.userId) {
        setCookie(impersonatedUserIdCookieKey, data.userId);
      } else {
        deleteCookie(impersonatedUserIdCookieKey);
      }
    } catch (error) {
      return handleServerFnError(error, {
        service: 'impersonation-api',
        function: 'setImpersonatedUserId',
        extra: {
          userId: data.userId,
        },
      });
    }
    return { success: true };
  });

const getImpersonatedUserId = createServerFn({
  method: 'GET',
}).handler(() => {
  return getCookie(impersonatedUserIdCookieKey);
});

export const getImpersonationStatus = createServerFn().handler(() => {
  const impersonatedUserId = getCookie(impersonatedUserIdCookieKey);
  return { isImpersonated: !!impersonatedUserId };
});
